import { useState } from "react";
import type { ReactNode } from "react";
import { ArrowRight, ChevronDown, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { PHASE_META, type FlowStep } from "./flowData";
import { RoleBadgeList } from "./RoleBadge";
import { ScreenshotPlaceholder } from "./ScreenshotPlaceholder";

interface FlowStepCardProps {
  step: FlowStep;
  children?: ReactNode;
}

/** Small uppercase section label used inside the card. */
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h4>
  );
}

/**
 * A per-step card. Collapsed by default — the header shows a one-line summary
 * plus the actors involved. Expanding reveals the impact detail (what happens,
 * who, screenshot) and any injected deep-dive. Highlighted steps get a violet
 * "magic" treatment to stand out (e.g. the fully-automated AI scan).
 */
export function FlowStepCard({ step, children }: FlowStepCardProps) {
  const [open, setOpen] = useState(false);
  const phase = PHASE_META[step.phase];
  const StepIcon = step.icon;
  const highlight = step.highlight;

  return (
    <div>
      <Card
        className={cn(
          "border-l-4 py-0 gap-0 overflow-hidden",
          phase.color.border,
          highlight &&
            "animate-step-border bg-gradient-to-br from-violet-50/70 to-transparent dark:from-violet-950/20",
          // Shimmer sweep only while collapsed — too distracting over expanded content.
          highlight && !open && "animate-step-shimmer"
        )}
      >
        {/* Header — click to expand/collapse */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="w-full text-left flex items-center gap-4 px-5 py-4 hover:bg-muted/40 transition-colors"
        >
          <div
            className={cn(
              "flex items-center justify-center size-11 rounded-full shrink-0",
              highlight ? "bg-violet-100 dark:bg-violet-900/40" : phase.color.bg
            )}
          >
            <StepIcon
              className={cn(
                "size-5",
                highlight ? "text-violet-600 dark:text-violet-300" : phase.color.text
              )}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className={`text-xs font-semibold uppercase tracking-wide ${phase.color.text}`}>
                Step {step.number}
              </span>
              {highlight && (
                <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 dark:bg-violet-900/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
                  <Sparkles className="size-3 animate-sparkle-twinkle" />
                  Fully automated
                </span>
              )}
            </div>
            <h3 className="text-lg font-semibold text-foreground leading-tight">
              {step.title}
            </h3>
            {!open && (
              <p className="text-sm text-muted-foreground mt-0.5">
                {step.summary}
              </p>
            )}
          </div>

          {/* Right side — actor(s) + expand chevron */}
          <div className="flex items-center gap-4 shrink-0">
            {!open && <RoleBadgeList roles={step.roles} />}
            <ChevronDown
              className={cn(
                "size-5 text-muted-foreground transition-transform",
                open && "rotate-180"
              )}
            />
          </div>
        </button>

        {/* Expanded body */}
        {open && (
          <div className="px-5 pb-6">
            <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
              {/* Left — impact detail */}
              <div className="space-y-5 min-w-0">
                <div className="space-y-1.5">
                  <SectionLabel>What happens</SectionLabel>
                  <p className="text-sm text-foreground leading-relaxed">
                    {step.whatHappens}
                  </p>
                </div>

                <div className="space-y-2">
                  <SectionLabel>Who</SectionLabel>
                  <RoleBadgeList roles={step.roles} />
                </div>
              </div>

              {/* Right — screenshot */}
              <div>
                <ScreenshotPlaceholder
                  caption={step.screenshotCaption}
                  src={step.screenshot}
                />
              </div>
            </div>

            {/* Injected deep-dive (e.g. AI pipeline under step 2) */}
            {children && (
              <div className="mt-6 rounded-lg border border-border bg-muted/40 p-4 md:p-5">
                {children}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* "What happens next" callout — only when expanded */}
      {open && step.betweenNote && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-muted/60 px-4 py-3">
          <ArrowRight className={`size-4 shrink-0 mt-0.5 ${phase.color.text}`} />
          <p className="text-sm text-muted-foreground leading-relaxed">
            {step.betweenNote}
          </p>
        </div>
      )}
    </div>
  );
}
